// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { EditPatientInterventionType } from "../../types";
import { updateTimelineIntervention } from "../../api";

export const useUpdateIntervention = (
  clinicId: string | undefined,
  patientId: string
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (inputData: {
      interventionId: string;
      interventionData: EditPatientInterventionType;
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updateTimelineIntervention(
          token,
          clinicId,
          patientId,
          inputData.interventionId,
          inputData.interventionData
        );
      } catch (error) {
        throw error;
      }
    },
    // Invalidate query cache
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["timeline-interventions", clinicId, patientId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Intervention updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Intervention could not be updated!"),
      });
    },
  });
};
